/**
 * NL-014 session-only funnel calculation receipt assembly.
 *
 * A funnel calculation receipt is a session-only planning-estimate provenance
 * record. This module owns the one production path that turns a live funnel
 * calculation into a `FunnelCalculationReceipt`: it derives caller-owned,
 * non-reversible input and output hashes from a canonical serialization, stamps
 * a caller-supplied clock, and delegates assembly and validation to
 * `buildFunnelCalculationReceipt`.
 *
 * It never persists anything and never reads the clock itself — the caller
 * passes `now`, exactly like every other NL-010/014 contract module. The receipt
 * it returns is kept only in session state, which `session-fields.ts` strips at
 * every serialization boundary (draft, share URL, stored plan, PDF, CRM).
 *
 * Hashing is delegated to `canonical-hash.ts`, which is real SHA-256 with a
 * distinct domain tag per slot. Two things follow, and both are relied upon:
 * a hash slot can never hold a bare number that the receipt contract would
 * (rightly) reject as a leaked figure, and `inputsHash`, `outputsHash` and
 * `result.resultHash` are independent even when they cover the same value.
 *
 * What the hashes do NOT do, stated plainly: SHA-256 of a small input space is
 * still enumerable by brute force, so a digest is a tamper-evidence and
 * change-detection device, not a confidentiality control. The confidentiality
 * guarantee comes from what is passed in — the caller hashes bands and enums,
 * never raw contact details or raw figures — and from the session-only
 * boundary, not from the digest.
 */
import {
  CANONICAL_HASH_DOMAINS,
  canonicalHash,
  canonicalize as canonicalizeValue,
  type CanonicalValue,
} from "./canonical-hash";
import {
  buildFunnelCalculationReceipt,
  type FunnelCalculationReceiptBuildResult,
  type PackageRecommendationComponentInput,
  type PriceEvidenceComponentInput,
  type SeasonalDemandComponentInput,
} from "./calculation-receipt-builder";
import type { FunnelStateVersion } from "./schema";

export const FUNNEL_CALCULATION_RECEIPT_SESSION_CONTRACT_VERSION =
  "nl014-funnel-calculation-receipt-session-v1";

export type SessionCalculationReceiptInput = {
  readonly funnelStateVersion: FunnelStateVersion;
  readonly outputScale: number;
  /** Caller-supplied clock; this module never reads the runtime clock. */
  readonly now: Date;
  /** Canonical, value-free description of the estimate inputs (hashed, never stored). */
  readonly inputs: CanonicalValue;
  /** Canonical, value-free description of the estimate outputs (hashed, never stored). */
  readonly outputs: CanonicalValue;
  /** Declared unit of the hashed result; defaults to `"planning-estimate"`. */
  readonly resultUnit?: string;
  readonly seasonalDemand?: SeasonalDemandComponentInput;
  readonly priceEvidence?: PriceEvidenceComponentInput;
  readonly packageRecommendation?: PackageRecommendationComponentInput;
};

/**
 * Re-exported so existing callers keep one import site. The implementation is
 * the shared canonical serializer — there is deliberately no second copy.
 */
export const canonicalize = canonicalizeValue;

function toUtcCalendarDate(now: Date): string {
  const year = now.getUTCFullYear().toString().padStart(4, "0");
  const month = (now.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = now.getUTCDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toCanonicalUtcTimestamp(now: Date): string {
  const hour = now.getUTCHours().toString().padStart(2, "0");
  const minute = now.getUTCMinutes().toString().padStart(2, "0");
  const second = now.getUTCSeconds().toString().padStart(2, "0");
  return `${toUtcCalendarDate(now)}T${hour}:${minute}:${second}Z`;
}

/**
 * Builds the session-only funnel calculation receipt for a live estimate.
 *
 * The result is exactly `buildFunnelCalculationReceipt`'s: `built` with a
 * ready receipt, or `blocked` with the upstream/contract reasons. Callers must
 * treat a `blocked` result as "no receipt this session" and never fabricate one.
 */
export function buildSessionCalculationReceipt(
  input: SessionCalculationReceiptInput,
): FunnelCalculationReceiptBuildResult {
  return buildFunnelCalculationReceipt({
    funnelStateVersion: input.funnelStateVersion,
    outputScale: input.outputScale,
    calculationTimestamp: toCanonicalUtcTimestamp(input.now),
    asOfDate: toUtcCalendarDate(input.now),
    // With no evidence component supplied, the only evidence behind the estimate
    // is the visitor's own inputs, entered now — so the session date is the
    // truthful anchor rather than a placeholder. When a component IS supplied it
    // carries its own dated receipt, and that receipt (not this field) is the
    // provenance for the external evidence.
    evidenceDate: toUtcCalendarDate(input.now),
    inputsHash: canonicalHash(CANONICAL_HASH_DOMAINS.receiptInputs, input.inputs),
    outputsHash: canonicalHash(CANONICAL_HASH_DOMAINS.receiptOutputs, input.outputs),
    result: {
      // Domain-separated from `outputsHash` even though it covers the same
      // value: an unseparated result hash would be byte-identical to the output
      // hash and would therefore carry no information of its own.
      resultHash: canonicalHash(CANONICAL_HASH_DOMAINS.receiptResult, input.outputs),
      unit: input.resultUnit ?? "planning-estimate",
    },
    ...(input.seasonalDemand === undefined ? {} : { seasonalDemand: input.seasonalDemand }),
    ...(input.priceEvidence === undefined ? {} : { priceEvidence: input.priceEvidence }),
    ...(input.packageRecommendation === undefined
      ? {}
      : { packageRecommendation: input.packageRecommendation }),
  });
}
